import { OpenHours, Weekday } from '@/__generated__/types';
import { DateTime } from 'luxon';

// Timestamp boundary between seconds and milliseconds formats.
// If a timestamp is less than this value, it is assumed to be in seconds (before ~2286-11-20T17:46:40Z).
// If greater, it is assumed to be in milliseconds (after ~1970-04-26T17:46:40Z).
const TIMESTAMP_SECONDS_MILLISECONDS_BOUNDARY = 10000000000;

export const convertTime = (timeValue: Date | DateTime | number | null, type: 'time' | 'timestamp' = 'timestamp'): number => {
    if (timeValue === null) {
        return 0;
    }

    // Handle different input types
    const time =
        timeValue instanceof DateTime
            ? timeValue
            : timeValue instanceof Date
              ? DateTime.fromJSDate(timeValue)
              : DateTime.fromMillis(timeValue);

    if (type === 'time') {
        // Convert to total minutes since midnight
        return time.hour * 60 + time.minute;
    }

    // Return timestamp in milliseconds
    return time.toMillis();
};

export const parseTime = (time: number): DateTime => {
    const formatTime = DateTime.now().startOf('day').plus({ minutes: time });
    return formatTime;
};

export const getWeekdayEnum = (weekday: string): Weekday | undefined => {
    return Object.values(Weekday).find((w) => w.toLowerCase() === weekday.toLowerCase());
};

/**
 * Converts timestamp to milliseconds if it's in seconds format
 * @param timestamp - The timestamp to convert
 * @returns Timestamp in milliseconds
 */
export const ensureMilliseconds = (timestamp: number): number => {
    return timestamp < TIMESTAMP_SECONDS_MILLISECONDS_BOUNDARY ? timestamp * 1000 : timestamp;
};

/**
 * Converts timestamp to seconds if it's in milliseconds format
 * @param timestamp - The timestamp to convert
 * @returns Timestamp in seconds
 */
export const ensureSeconds = (timestamp: number): number => {
    return timestamp > TIMESTAMP_SECONDS_MILLISECONDS_BOUNDARY ? Math.floor(timestamp / 1000) : timestamp;
};

export const getDisabledWeekdays = (openHours?: OpenHours[]): Weekday[] => {
    if (!openHours) {
        return [];
    }
    const allWeekdays: Weekday[] = [
        Weekday.Monday,
        Weekday.Tuesday,
        Weekday.Wednesday,
        Weekday.Thursday,
        Weekday.Friday,
        Weekday.Saturday,
        Weekday.Sunday
    ];

    const openWeekdaysSet = new Set<Weekday>();
    openHours.forEach((hour) => {
        hour.weekDays.forEach((day) => { if (day !== null) openWeekdaysSet.add(day); });
    });

    return allWeekdays.filter((day) => !openWeekdaysSet.has(day));
};
